import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '../../lib/utils';
import { Icon } from '../icon';

export interface ResizableAsideProps {
  /** Main content rendered on the left (flex-1). */
  children: ReactNode;
  /** Aside content rendered on the right. */
  aside: ReactNode;
  /** Initial width of the aside in px (default 320). */
  initialWidth?: number;
  /** Minimum width of the aside in px (default 240). */
  minWidth?: number;
  /** Maximum width of the aside in px (default 640). */
  maxWidth?: number;
  /**
   * localStorage key for persisting the chosen width AND collapsed state
   * across sessions. The key is used as-is for the width; the `:collapsed`
   * suffix is appended for the collapsed flag.
   */
  storageKey?: string;
  /** Override the gap between main content and the divider (Tailwind class). */
  gapClassName?: string;
  /**
   * Drag threshold (px below `minWidth`) at which the panel snaps closed.
   * Default 40 — i.e. dragging the divider 40px past the min width collapses.
   */
  collapseThreshold?: number;
}

const COLLAPSED_STRIP_WIDTH = 32;
const DEFAULT_COLLAPSE_THRESHOLD = 40;

const clampWidth = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const readPersistedWidth = (
  key: string | undefined,
  bounds: { min: number; max: number; fallback: number }
): number => {
  const { min, max, fallback } = bounds;
  if (!key || typeof window === 'undefined') {
    return fallback;
  }
  try {
    const saved = window.localStorage.getItem(key);
    if (!saved) {
      return fallback;
    }
    const parsed = parseInt(saved, 10);
    if (Number.isNaN(parsed)) {
      return fallback;
    }

    return clampWidth(parsed, min, max);
  } catch {
    return fallback;
  }
};

const readPersistedCollapsed = (key: string | undefined): boolean => {
  if (!key || typeof window === 'undefined') {
    return false;
  }
  try {
    return window.localStorage.getItem(`${key}:collapsed`) === '1';
  } catch {
    return false;
  }
};

// Permanent grab-handle pill in brand teal (secondary token). Pseudo-element
// `before` is the pill, `after` is the faint full-height guide line.
const handleClasses = cn(
  'relative w-2 bg-transparent transition-colors duration-150',
  'after:content-[""] after:absolute after:inset-y-0 after:left-1/2',
  'after:w-[0.5px] after:rounded-[1px] after:bg-neutral-border',
  'after:transition-colors after:duration-150 after:-translate-x-1/2',
  'before:content-[""] before:absolute before:top-1/2 before:left-1/2',
  'before:w-[5px] before:h-[100px] before:max-h-[60%] before:rounded-[3px]',
  'before:bg-neutral-border before:transition before:duration-150',
  'before:-translate-x-1/2 before:-translate-y-1/2 before:z-[1]',
  'hover:after:bg-secondary focus-visible:after:bg-secondary',
  'hover:before:bg-secondary focus-visible:before:bg-secondary',
  'hover:before:shadow-[0_0_0_2px_oklch(var(--secondary)/0.3)]',
  'focus-visible:before:shadow-[0_0_0_2px_oklch(var(--secondary)/0.3)]',
  'focus-visible:outline-none'
);

const collapsedStripClasses = cn(
  'bg-muted border border-solid border-neutral-border rounded-md text-muted-foreground cursor-pointer',
  'transition-colors duration-150',
  'hover:bg-secondary-minimal hover:text-foreground hover:border-secondary',
  'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-secondary'
);

/**
 * Two-column layout (main content + aside) with a draggable vertical handle
 * between them. Dragging far enough past `minWidth` snaps the aside closed
 * into a thin drawer strip — clicking the strip (or the handle) re-opens it
 * at its last-known width. Width and collapsed state are persisted to
 * localStorage when `storageKey` is supplied.
 */
export const ResizableAside: FC<ResizableAsideProps> = ({
  children,
  aside,
  initialWidth = 320,
  minWidth = 240,
  maxWidth = 640,
  storageKey,
  gapClassName = 'gap-3',
  collapseThreshold = DEFAULT_COLLAPSE_THRESHOLD,
}) => {
  const [width, setWidth] = useState<number>(() =>
    readPersistedWidth(storageKey, {
      min: minWidth,
      max: maxWidth,
      fallback: initialWidth,
    })
  );
  const [collapsed, setCollapsed] = useState<boolean>(() =>
    readPersistedCollapsed(storageKey)
  );

  const widthRef = useRef(width);
  widthRef.current = width;
  const collapsedRef = useRef(collapsed);
  collapsedRef.current = collapsed;

  const draggingRef = useRef(false);
  const didDragRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const persistWidth = useCallback(
    (value: number) => {
      if (!storageKey || typeof window === 'undefined') {
        return;
      }
      try {
        window.localStorage.setItem(storageKey, String(Math.round(value)));
      } catch {
        // ignore — localStorage may be unavailable (private mode, quota)
      }
    },
    [storageKey]
  );

  const persistCollapsed = useCallback(
    (value: boolean) => {
      if (!storageKey || typeof window === 'undefined') {
        return;
      }
      try {
        window.localStorage.setItem(
          `${storageKey}:collapsed`,
          value ? '1' : '0'
        );
      } catch {
        // ignore
      }
    },
    [storageKey]
  );

  const expandToWidth = useCallback(
    (next: number) => {
      const clamped = clampWidth(next, minWidth, maxWidth);
      setWidth(clamped);
      setCollapsed(false);
      persistWidth(clamped);
      persistCollapsed(false);
    },
    [maxWidth, minWidth, persistCollapsed, persistWidth]
  );

  const collapse = useCallback(() => {
    setCollapsed(true);
    persistCollapsed(true);
  }, [persistCollapsed]);

  const handleHandleClick = useCallback(() => {
    // mousedown -> drag -> mouseup raises a synthetic click; swallow it so
    // the click only toggles when the user did not actually drag.
    if (didDragRef.current) {
      didDragRef.current = false;

      return;
    }
    if (collapsedRef.current) {
      expandToWidth(widthRef.current);
    } else {
      collapse();
    }
  }, [collapse, expandToWidth]);

  const onMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    draggingRef.current = true;
    didDragRef.current = false;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const onMouseMove = (event: MouseEvent) => {
      if (!draggingRef.current || !containerRef.current) {
        return;
      }
      const containerRight = containerRef.current.getBoundingClientRect().right;
      const desired = containerRight - event.clientX;

      if (Math.abs(desired - widthRef.current) > 2) {
        didDragRef.current = true;
      }

      if (desired < minWidth - collapseThreshold) {
        if (!collapsedRef.current) {
          setCollapsed(true);
          collapsedRef.current = true;
        }

        return;
      }

      const clamped = clampWidth(desired, minWidth, maxWidth);
      if (collapsedRef.current) {
        setCollapsed(false);
        collapsedRef.current = false;
      }
      setWidth(clamped);
      // Keep the ref in lockstep with state so the synchronous mouseup that
      // can fire on the same tick persists the final value, not the prior one.
      widthRef.current = clamped;
    };

    const onMouseUp = () => {
      if (!draggingRef.current) {
        return;
      }
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      persistWidth(widthRef.current);
      persistCollapsed(collapsedRef.current);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      // If we unmount mid-drag (e.g. route change), the cursor/user-select
      // overrides applied on mousedown would otherwise leak globally.
      if (draggingRef.current) {
        draggingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
  }, [collapseThreshold, maxWidth, minWidth, persistCollapsed, persistWidth]);

  const onHandleDoubleClick = useCallback(() => {
    expandToWidth(initialWidth);
  }, [expandToWidth, initialWidth]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (collapsed) {
          expandToWidth(widthRef.current);
        } else {
          collapse();
        }
      }
    },
    [collapse, collapsed, expandToWidth]
  );

  const onStripClick = useCallback(() => {
    expandToWidth(widthRef.current);
  }, [expandToWidth]);

  return (
    <div
      ref={containerRef}
      className={cn('flex', gapClassName)}
      data-slot={'resizable-aside'}
      data-testid={'resizable-aside'}
    >
      <div className={cn('min-w-0 flex-1')}>{children}</div>
      <div
        role={'separator'}
        aria-orientation={'vertical'}
        aria-label={collapsed ? 'Expand panel' : 'Resize panel'}
        aria-valuenow={collapsed ? 0 : Math.round(width)}
        aria-valuemin={0}
        aria-valuemax={maxWidth}
        tabIndex={0}
        onMouseDown={onMouseDown}
        onClick={handleHandleClick}
        onDoubleClick={onHandleDoubleClick}
        onKeyDown={onKeyDown}
        className={cn(
          handleClasses,
          'shrink-0 self-stretch',
          collapsed ? 'cursor-pointer' : 'cursor-col-resize'
        )}
        data-testid={'resizable-aside-handle'}
      />
      {collapsed ? (
        <button
          type={'button'}
          onClick={onStripClick}
          aria-label={'Expand panel'}
          className={cn(
            collapsedStripClasses,
            'shrink-0 self-stretch flex items-center justify-center'
          )}
          style={{ width: `${COLLAPSED_STRIP_WIDTH}px` }}
          data-testid={'resizable-aside-collapsed-strip'}
        >
          <Icon name={'notebook-tabs'} size={'md'} aria-hidden={true} />
        </button>
      ) : (
        <div
          className={cn('shrink-0 flex flex-col gap-4 [:empty]:hidden')}
          style={{ width: `${width}px` }}
          data-testid={'resizable-aside-content'}
        >
          {aside}
        </div>
      )}
    </div>
  );
};

export default ResizableAside;
